// libraries
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Moment } from 'moment';

import { Button, Divider, Form, FormInstance, Input, notification, Select, Switch } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { FieldData } from 'rc-field-form/lib/interface';
import * as Colors from '@ant-design/colors';

// services
import * as ActivityService from '../../../../services/bb-api/activity';
import * as MunicipalityService from '../../../../services/bb-api/municipality';
import * as StatusService from '../../../../services/bb-api/status';

// components
import { MomentGenerator } from '../../../shared/moment-generator';
import { DateSelectorList } from '../../../shared/date-selector-list';
import { ImageTool } from '../../../shared/image-tool';
import { AddressSearchBar, Address } from '../../../shared/address-search-bar';

import { txt } from '../../../../locales/i18n';
import { useDeviceSize } from '../../../../hooks/device-size';

const initialValues: any = {
	status: 2,
	ticketPrice: 0,
	approved: 0,
};

export function CreateActivityPage(): JSX.Element {
	const [municipalities, setMunicipalities] = useState<any[]>([]);
	const [categories, setCategories] = useState<any[]>([]);
	const [statuses, setStatuses] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [imageSource, setImageSource] = useState<string>('');
	const isMobileDevice: boolean = useDeviceSize();

	const formRef = useRef<any>(null);
	const imageToolRef = useRef<any>(null);
	const history = useHistory();

	useEffect(() => {
		getFormData();
	}, []);

	async function getFormData(): Promise<void> {
		setLoading(true);

		try {
			const categories: any[] = await ActivityService.getCategories();
			setCategories(categories);

			const municipalities: any[] = await MunicipalityService.query();
			setMunicipalities(municipalities);

			const statuses: any[] = await StatusService.query();
			setStatuses(statuses);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	function handleCategoryChange(values: number[]): void {
		const form = formRef.current;

		if (values.length === 0) {
			form.setFields([
				{
					name: 'categories',
					errors: [txt('notification.minimum x required', { amount: 1 })],
				},
			]);
			return;
		}

		form.setFields([{ name: 'categories', errors: [] }]);
	}

	function setMomentSets(momentSets: [Moment, Moment][]): void {
		formRef.current.setFields([{ name: 'dates', value: momentSets }]);
	}

	async function handleCreateActivity(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		form.setFields([{ name: 'lat', errors: [] }]);
		const lat = form.getFieldValue('lat') ?? null;

		if (lat === null) {
			form.setFields([{ name: 'lat', errors: [txt('global.required')] }]);
			return;
		}

		if (Boolean(values?.municipalityId) === false) {
			form.setFields([
				{ name: 'municipalityId', errors: [txt('global.select municipality')] },
			]);

			return;
		}

		if (Boolean(values?.categories?.length) === false) {
			form.setFields([
				{
					name: 'categories',
					errors: [txt('notification.minimum x required', { amount: 1 })],
				},
			]);

			return;
		}

		const dates: [Moment, Moment][] | undefined = values?.dates?.filter((dateSet: any) => {
			return Array.isArray(dateSet);
		});

		if (dates === undefined || dates.length === 0) {
			notification.error({ message: 'Minimum 1 date is required' });
			return;
		}

		values.dates = dates;

		try {
			const canvas = imageToolRef.current.getImage();
			values.imageBase64 = canvas.toDataURL('image/jpeg');
		} catch (error) {
			notification.error({ message: txt('notification.image missing') });
			return;
		}

		try {
			const response = await ActivityService.create(values);
			notification.success({ message: txt('notification.create successful') });
			history.push(`/activities/${response.activityId}`);
		} catch (error) {
			notification.error({ message: txt('notification.create failed') });
		}
	}

	const handleAddressSelect = (address: Address | null) => {
		const form = formRef.current;
		if (form === null) return;

		if (address === null) {
			const addressFields: FieldData[] = [
				{ name: 'city', value: null },
				{ name: 'address', value: null },
				{ name: 'postalCode', value: null },
				{ name: 'lat', value: null },
				{ name: 'lng', value: null },
			];

			return form?.setFields(addressFields);
		}

		const addressFields: FieldData[] = [
			{ name: 'city', value: address.city },
			{ name: 'address', value: address.name },
			{ name: 'postalCode', value: address.postalCode },
			{ name: 'lat', value: address.lat },
			{ name: 'lng', value: address.lng },
		];

		form?.setFields(addressFields);
	};

	return (
		<main>
			<StyledForm
				ref={formRef}
				initialValues={initialValues}
				onFinish={handleCreateActivity}
				labelCol={{ span: 5 }}
				isMobileDevice={isMobileDevice}
			>
				<div className="form-container">
					<div className="fields-container">
						<Form.Item label={txt('global.title')} name="title">
							<Input required />
						</Form.Item>

						<Form.Item label={txt('global.organizer')} name="organizer">
							<Input required />
						</Form.Item>

						<Form.Item label={txt('global.location')} name="location">
							<Input required />
						</Form.Item>

						<Form.Item label={txt('global.search')}>
							<AddressSearchBar onSelect={handleAddressSelect} />
						</Form.Item>

						<Form.Item name="address" label={txt('global.address')}>
							<Input placeholder={txt('global.address')} disabled />
						</Form.Item>

						<Form.Item label={txt('global.city')}>
							<div className="multi-input-container">
								<Form.Item name="postalCode" noStyle>
									<Input
										placeholder={txt('global.postal code')}
										disabled
										className="postalcode-input"
									/>
								</Form.Item>

								<Form.Item name="city" noStyle>
									<Input placeholder={txt('global.city')} disabled />
								</Form.Item>
							</div>
						</Form.Item>

						<Form.Item label="GPS">
							<div className="multi-input-container">
								<Form.Item name="lat" noStyle>
									<Input disabled placeholder="latitude" />
								</Form.Item>

								<Form.Item name="lng" noStyle>
									<Input disabled placeholder="longitude" />
								</Form.Item>
							</div>
						</Form.Item>

						<Form.Item label={txt('global.municipality')} name="municipalityId">
							<Select placeholder="Please select" loading={loading}>
								{municipalities.map((municipality: any) => (
									<Select.Option key={municipality.id} value={municipality.id}>
										{municipality.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label={txt('global.price')} name="ticketPrice">
							<Input type="number" required />
						</Form.Item>

						<Form.Item label={txt('global.categories')} name="categories">
							<Select
								mode="multiple"
								loading={loading}
								placeholder="Please select"
								onChange={handleCategoryChange}
							>
								{categories.map((category: any) => (
									<Select.Option key={category.id} value={category.id}>
										{category.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item name="url" label={txt('global.website')}>
							<Input />
						</Form.Item>

						<Form.Item name="status" label="Status">
							<Select loading={loading}>
								{statuses.map((status: any) => (
									<Select.Option key={status.id} value={status.id}>
										{status.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</div>

					<Form.Item label={txt('global.text')} name="body">
						<Input.TextArea required />
					</Form.Item>

					<Form.Item label={txt('global.image')}>
						<ImageTool
							refProxy={imageToolRef}
							imageSource={imageSource}
							setImageSource={setImageSource}
						/>
					</Form.Item>

					<Form.Item label={txt('global.dates')}>
						<MomentGenerator setMomentSets={setMomentSets} />
						<Divider />
						<DateSelectorList name="dates" />
					</Form.Item>
				</div>

				<div className="submit-container">
					<Form.Item name="approved" valuePropName="checked">
						<Switch
							checkedChildren={txt('global.approved')}
							unCheckedChildren={txt('global.not approved')}
							defaultChecked
						/>
					</Form.Item>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="primary"
						htmlType="submit"
						loading={loading}
					>
						{txt('global.create')} <PlusOutlined />
					</Button>

					<Button size={isMobileDevice ? 'large' : 'middle'} onClick={history.goBack}>
						{txt('global.cancel')} <CloseOutlined />
					</Button>
				</div>
			</StyledForm>
		</main>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	padding: 2rem;
	display: flex;
	flex-direction: column;
	gap: 2em;

	.input-container {
		display: grid;
		gap: 0rem 2rem;

		${({ isMobileDevice }) => {
			if (isMobileDevice) return;
			return 'grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));';
		}}
	}

	.multi-input-container {
		display: flex;

		> * {
			flex: 1;
		}

		.postalcode-input {
			width: 12ch;
			flex: none;
		}
	}

	.submit-container {
		display: flex;
		gap: 1rem;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		button.ant-switch {
			zoom: 1.4;

			span {
				font-size: 0.65em;
			}

			&.ant-switch-checked {
				background: ${Colors.green.primary};
			}
		}
	}
`;
